import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  StatusCandidatePositionOptions,
  candidatesApi,
  useUpdateCandidatePositionStatusMutation,
} from '$store/modules/candidates';
import { segmentTrack } from '$utils/handleSegment';
import ActiveValues from '$constants/ActiveValues';
import { companiesApi } from '$store/modules/companies';
import { CandidateData } from '$types/candidates';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import { SIMERA_FEE } from '$constants/SimeraOperations';
import capitalizedName from '$utils/capitalizedName';
import { countriesCodes } from '$constants/countriesCodes';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';
import SPKCandidateHiddenSkills from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateHiddenSkills';
import SPKCandidateCardSkill from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateSkills';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { ReactComponent as Star } from '$icons/uncolored/star.svg';
import { ReactComponent as MakeOffer } from '$icons/uncolored/makeOffer.svg';
import { ReactComponent as ThumbDown } from '$icons/uncolored/thumbs-down.svg';
import { ReactComponent as Interview } from '$icons/uncolored/comments.svg';
import { ReactComponent as Verified } from '$icons/uncolored/verified.svg';
import { ReactComponent as View } from '$icons/uncolored/eye.svg';
import { toast } from 'react-toastify';
import MakeOfferConfirmModal from '$components/Positions/CandidateSummaryCardList/CandidateSummaryCard/MakeOfferConfirmModal/MakeOfferConfirmModal';
import { singlePositionSlice } from '$store/modules/position/position.reducer';

import SPKEmployerCandidateShowcase from './SPKEmployerCandidateShowcase/SPKEmployerCandidateShowcase';

interface Props {
  candidate: CandidateData;
  setSwitchingId: Dispatch<SetStateAction<number | null>>;
  position: PositionInternalDto;
}

const SPKEmployerPositionCandidateCard: FC<Props> = ({
  candidate,
  setSwitchingId,
  position,
}) => {
  const { positionId: positionIdFromParam } = useParams();
  const positionId = (
    positionIdFromParam ? Number(positionIdFromParam) : position.Id
  ) as number;
  const [showMakeOfferModal, setMakeOfferModal] = useState<boolean>(false);
  const dispatch = useDispatch();

  const [updateCandidatePositionStatus] =
    useUpdateCandidatePositionStatusMutation();

  const [overflowStart, setOverflowStart] = useState<number>();
  const [hiddenSkills, setHiddenSkills] = useState<
    CandidateData['CandidateSkills']
  >([]);
  const [visibleSkills, setVisibleSkills] = useState<
    CandidateData['CandidateSkills']
  >([]);

  const [t, i18n] = useTranslation('global');

  const yearsOfExperience = useMemo(() => {
    return computeYearsOfExperience(
      candidate.CandidateWorkExperience as CandidateData['CandidateWorkExperience']
    );
  }, [candidate.CandidateWorkExperience]);

  const countryEmoji = useMemo(() => {
    return countriesCodes.find(
      (singleCountry) =>
        singleCountry.name.toLowerCase() ===
        candidate?.ResidenceCountry?.toLowerCase()
    )?.emoji;
  }, [candidate?.ResidenceCountry]);

  useEffect(() => {
    setMakeOfferModal(false);
  }, [candidate]);

  const handleMakeOfferModal = (show: boolean) => setMakeOfferModal(show);

  // const interviewAgainHandler = () => {
  //   setShowModalConfirmationInterview(true);
  // };

  const skillsContainerRef = useRef<HTMLDivElement | null>(null);

  const changeCandidatePositionStatusHandler = (
    newStatus: StatusCandidatePositionOptions
  ) => {
    if ('CandidatesPositionStatus' in candidate) {
      if (!candidate.CandidatesPositionStatus) return;
      updateCandidatePositionStatus({
        data: {
          CandidatePositionStatusId: candidate.CandidatesPositionStatus[0].Id,
          Status: newStatus,
        },
        lang: i18n.language as 'en' | 'es',
      }).then(() => {
        setSwitchingId(candidate.Id);
        dispatch(candidatesApi.util.invalidateTags(['Candidates']));
        dispatch(companiesApi.util.invalidateTags(['PositionsList']));
        if (newStatus === 'new') {
          segmentTrack(`Candidate Removed from Shortlist`, {
            candidate_id: candidate.Id,
            position_id: positionId,
          });
        } else if (newStatus === 'shortlisted') {
          segmentTrack(`Candidate Added to Shortlist`, {
            candidate_id: candidate.Id,
            position_id: positionId,
          });
        }
      });
    }
  };

  const onOverflowHandler = (index: number) => {
    setOverflowStart(index);
  };

  const candidateScore = useMemo(() => {
    return position?.CandidatesPositionStatus?.find(
      (singleCandidate) => singleCandidate.Candidate.Id === candidate.Id
    )?.Candidate?.Score;
  }, [candidate.Id, position.CandidatesPositionStatus]);

  const demandScoreColor = useMemo(() => {
    const scoreColor = {
      low: 'bg-[#FCDAC4] text-[#A44101]',
      medium: 'bg-[#CFD0FF] text-[#2C30D7]',
      high: 'bg-[#BFEFEC] text-[#016F68]',
    };

    if (candidateScore > 90) return scoreColor.high;
    if (candidateScore > 80) return scoreColor.medium;
    return scoreColor.low;
  }, [candidateScore]);

  const makeOfferHandler = useCallback(() => {
    if (!position.CandidatesPositionStatus) return;
    const existingOffer = position.CandidatesPositionStatus.find(
      (singleCandidatePositionStatus) =>
        (singleCandidatePositionStatus.Status as unknown as ActiveValues) ===
        ActiveValues.OfferSent
    );

    if (existingOffer) {
      toast(t(t('CandidatePositionSummaryCard.ExistingOffer')), {
        type: 'error',
        hideProgressBar: false,
      });
    } else {
      setMakeOfferModal(true);
    }
  }, [position.CandidatesPositionStatus, t]);

  useEffect(() => {
    if (!candidate.CandidateSkills) return;

    if (!overflowStart && candidate.CandidateSkills) {
      setVisibleSkills(candidate.CandidateSkills);
      return;
    }

    const visible = candidate.CandidateSkills.slice(0, overflowStart);
    const hidden = candidate.CandidateSkills.slice(overflowStart);

    setVisibleSkills(visible);
    setHiddenSkills(hidden);
  }, [candidate.CandidateSkills, overflowStart]);

  return (
    <li className="flex flex-col md:flex-row">
      {/* Left section */}
      <div className="w-full md:w-6/12 p-4 bg-white rounded-t-xl md:rounded-tr-none md:rounded-l-xl">
        {/* Name, last job and country */}
        <div className="flex flex-row gap-4 mb-8 items-center">
          <div className="w-10 min-w-10">
            <DefaultIcon2
              isEmployer
              firstName={candidate.User?.FirstName}
              lastName={candidate.User?.LastName}
              fontSize="text-xl"
              hideBorder
              picture={candidate?.User?.ProfilePicture?.Url}
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:divide-x divide-gray-400 *:px-4 -mx-4 sm:items-center">
            <div className="flex font-bold text-base">
              {`${capitalizedName(candidate.User?.FirstName)} ${capitalizedName(
                candidate.User?.LastName
              )}`}
            </div>
            <div className="font-bold flex flex-row items-center">
              {candidate?.CandidateWorkExperience?.[0]?.JobTitle && (
                <span className="mr-4">
                  {candidate?.CandidateWorkExperience?.[0]?.JobTitle}
                </span>
              )}
              {candidate.ResidenceCountry && (
                <span className="text-nowrap font-light text-xs border border-neutral-200 text-neutral-600 rounded-md px-2 py-1 flex items-center self-center mr-2">
                  {countryEmoji} {candidate.ResidenceCountry}
                </span>
              )}
              <span
                className={`md:text-nowrap text-xs px-2 py-1 items-center flex font-light rounded self-center ${demandScoreColor}`}
              >
                {t('PositionCard.Demand')} {candidateScore}
              </span>
            </div>
          </div>
        </div>

        {/* General Info */}
        <div className="text-sm mb-2">
          <div className="flex flex-row divide-x divide-gray-400 *:px-2 -mx-2">
            {candidate?.CandidateWorkExperience?.[0]?.Company && (
              <span className="flex flex-wrap text-black/50">
                Highlight:{' '}
                <span className="text-black ml-1">
                  {candidate?.CandidateWorkExperience?.[0]?.Company}
                </span>
              </span>
            )}
            <span>
              <span className="text-black/50">Exp:</span> {yearsOfExperience}+
              yrs
            </span>
            <span>
              <span className="text-black/50">Monthly Salary (USD):</span> $
              {(candidate?.CandidateJobInterest?.SalaryUpperBound || 0) +
                SIMERA_FEE}
              /m USD
            </span>
          </div>
        </div>

        {/* Skills */}
        <div className="flex flex-row items-center gap-2 mb-8">
          <div
            className="flex overflow-x-hidden gap-2 text-xs font-semibold text-gray-500 max-w-full"
            ref={skillsContainerRef}
          >
            {candidate.IsSimeraAICertified && (
              <span className="text-xs rounded border border-gray-300 p-1 text-nowrap flex flex-row gap-2">
                <span>Simera AI Certified</span>
                <Verified className="w-4 h-4 fill-spk-brightBlue" />
              </span>
            )}
            {visibleSkills &&
              visibleSkills
                .slice(0, overflowStart)
                .map((singleSkill, index) => {
                  return (
                    <SPKCandidateCardSkill
                      key={`${singleSkill.Skill.Title}_${singleSkill.Id}_${positionId}`}
                      skill={singleSkill.Skill.Title}
                      index={index}
                      containerRef={
                        skillsContainerRef.current as HTMLDivElement
                      }
                      setOverflowIndex={onOverflowHandler}
                      isVerified={singleSkill.IsVerified}
                    />
                  );
                })}
          </div>
          {hiddenSkills && hiddenSkills.length > 0 && (
            <SPKCandidateHiddenSkills
              hiddenSkills={hiddenSkills.map(({ Id, Skill, IsVerified }) => ({
                Id,
                Skill: { title: Skill.Title },
                IsVerified,
              }))}
              uniqueId={positionId}
            />
          )}
        </div>

        <button
          type="button"
          className="flex flex-row items-center gap-2 text-sm text-spk-highContrast"
          onClick={() => {
            dispatch(
              singlePositionSlice.actions.setSelectedCandidate(candidate)
            );
            dispatch(singlePositionSlice.actions.setOpenFullSPKProfile(true));
            dispatch(singlePositionSlice.actions.setIsLoadingIframe(true));
          }}
        >
          <View className="fill-spk-brightBlue w-4 h-4" />
          {t('PositionCard.FullProfile')}{' '}
        </button>
      </div>
      <div className=" bg-white items-center hidden md:flex flex-row py-4">
        <div className="border-l border-spk-neutral-40 h-full" />
      </div>
      <div className="w-full md:w-6/12 flex flex-row">
        {/* Middle section */}
        <div className="w-8/12 py-4 px-12 bg-white rounded-bl-xl md:rounded-bl-none">
          <p className="font-bold mb-2">Showcase</p>
          <SPKEmployerCandidateShowcase
            candidate={candidate}
            positionId={position.Id}
          />
        </div>

        {/* Right section */}
        <div className="w-4/12 bg-spk-neutral-10 rounded-br-xl md:rounded-r-xl">
          <div className="h-48 font-bold gap-6 flex flex-col py-4 md:py-8 px-4 text-xs">
            <button
              type="button"
              className="text-spk-brightBlue flex flex-row gap-2 items-center"
              onClick={() => {
                dispatch(
                  singlePositionSlice.actions.setSelectedCandidate(candidate)
                );
                dispatch(
                  singlePositionSlice.actions.setOpenConfirmationInterviewModal(
                    true
                  )
                );
              }}
            >
              <span className="bg-spk-brightBlue/15 px-4 py-2 rounded-full flex flex-row gap-2">
                <Interview className="fill-spk-brightBlue w-4 h-4" />
                {t('EmployerPositions.RequestInterview')}
              </span>
            </button>
            {!(
              candidate.CandidatesPositionStatus[0].Status ===
                ActiveValues.ShortList ||
              candidate.CandidatesPositionStatus[0].Status ===
                ActiveValues.requestInterview
            ) && (
              <button
                type="button"
                className="text-spk-brightBlue flex flex-row gap-2 items-center px-4"
                onClick={() =>
                  changeCandidatePositionStatusHandler('shortlisted')
                }
              >
                <Star className="fill-spk-brightBlue w-4 h-4" />
                {t('EmployerPositions.ShortList')}
              </button>
            )}
            {candidate.CandidatesPositionStatus[0].Status ===
              ActiveValues.ShortList && (
              <button
                type="button"
                className="text-spk-brightBlue flex flex-row gap-2 items-center px-4"
                onClick={() => changeCandidatePositionStatusHandler('new')}
              >
                <Star className="fill-spk-brightBlue w-4 h-4" />
                {t('EmployerPositions.RemoveShortList')}
              </button>
            )}
            <button
              type="button"
              className="text-spk-brightBlue flex flex-row gap-2 items-center px-4"
              onClick={makeOfferHandler}
            >
              <MakeOffer className="fill-spk-brightBlue w-4 h-4" />
              {t('EmployerPositions.MakeOffer')}
            </button>
            <button
              type="button"
              className="text-spk-brightRed flex flex-row gap-2 items-center px-4"
              onClick={() => {
                dispatch(
                  singlePositionSlice.actions.setSelectedCandidate(candidate)
                );
                dispatch(singlePositionSlice.actions.setOpenNotAFitModal(true));
              }}
            >
              <ThumbDown className="fill-spk-brightRed w-4 h-4" />
              {t('EmployerPositions.NotAFit')}
            </button>
          </div>
        </div>
      </div>
      {/* Modals */}
      <MakeOfferConfirmModal
        modalIsOpen={showMakeOfferModal}
        handleClose={handleMakeOfferModal}
        candidatePositionStatusId={candidate.CandidatesPositionStatus[0].Id}
        candidateId={candidate.Id}
        setSwitchingId={setSwitchingId as () => void}
      />
    </li>
  );
};

export default SPKEmployerPositionCandidateCard;
